import {
  useEffect,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarMonthRounded,
  RestoreRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import IndustrialWorkflowDatePicker, {
  toDateOnlyInputValue,
} from './IndustrialWorkflowDatePicker';
import {
  adjustIndustrialWorkflowTaskDeadline,
  previewIndustrialWorkflowTaskDeadline,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowCase,
  IndustrialWorkflowTask,
  IndustrialWorkflowTaskDeadlineAdjustmentMode,
  IndustrialWorkflowTaskDeadlineAdjustmentResult,
} from '../types/industrialWorkflows';

type Props = {
  workflowCase: IndustrialWorkflowCase | null;
  task: IndustrialWorkflowTask | null;
  open: boolean;
  onClose: () => void;
  onAdjusted: (
    result:
      IndustrialWorkflowTaskDeadlineAdjustmentResult,
  ) => Promise<void> | void;
};

function getErrorMessage(
  error: unknown,
) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function formatDateOnly(
  value: string | null | undefined,
) {
  if (!value) {
    return 'Sin fecha';
  }

  const parsedDate = new Date(
    `${value.slice(0, 10)}T12:00:00`,
  );

  if (Number.isNaN(parsedDate.getTime())) {
    return 'Fecha no disponible';
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'short',
    },
  ).format(parsedDate);
}

export default function IndustrialWorkflowTaskDeadlineDialog({
  workflowCase,
  task,
  open,
  onClose,
  onAdjusted,
}: Props) {
  const { enqueueSnackbar } =
    useSnackbar();

  const [
    newDueOn,
    setNewDueOn,
  ] = useState('');

  const [
    reason,
    setReason,
  ] = useState('');

  const [
    adjustmentMode,
    setAdjustmentMode,
  ] =
    useState<IndustrialWorkflowTaskDeadlineAdjustmentMode>(
      'manual',
    );

  const [
    acceptConflict,
    setAcceptConflict,
  ] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setNewDueOn(
      toDateOnlyInputValue(
        task?.due_at,
      ),
    );

    setReason('');
    setAdjustmentMode('manual');
    setAcceptConflict(false);
  }, [
    open,
    task?.due_at,
    task?.id,
  ]);

  const previewQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-task-deadline-preview',
        workflowCase?.id,
        task?.id,
        newDueOn,
      ],
      queryFn: () =>
        previewIndustrialWorkflowTaskDeadline(
          workflowCase?.id as string,
          task?.id as string,
          {
            new_due_on:
              newDueOn || null,
          },
        ),
      enabled: Boolean(
        open
        && workflowCase?.id
        && task?.id,
      ),
      retry: false,
    });

  const requiresConflictAcceptance =
    Boolean(
      previewQuery.data
        ?.requires_explicit_conflict_acceptance,
    );

  const shouldHighlightReason =
    Boolean(
      previewQuery.data
      && (
        (newDueOn || null)
        !== (
          previewQuery.data
            .current_due_on
          ?? null
        )
      )
      && !reason.trim(),
    );

  const adjustMutation =
    useMutation({
      mutationFn: () =>
        adjustIndustrialWorkflowTaskDeadline(
          workflowCase?.id as string,
          task?.id as string,
          {
            expected_stage_entry_token:
              workflowCase?.current_stage_entry_token as string,

            new_due_on:
              newDueOn || null,

            reason:
              reason.trim(),

            adjustment_mode:
              adjustmentMode,

            accept_conflict:
              acceptConflict,
          },
        ),
      onSuccess: async (
        result,
      ) => {
        await onAdjusted(
          result,
        );

        enqueueSnackbar(
          'Plazo de tarea actualizado correctamente.',
          {
            variant: 'success',
          },
        );

        onClose();
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const handleAdjustmentModeChange = (
    value:
      IndustrialWorkflowTaskDeadlineAdjustmentMode,
  ) => {
    setAdjustmentMode(value);
    setAcceptConflict(false);

    if (
      value === 'restore_default'
    ) {
      setNewDueOn(
        previewQuery.data
          ?.default_due_on
        ?? '',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={
        adjustMutation.isPending
          ? undefined
          : onClose
      }
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Ajustar plazo de tarea
      </DialogTitle>

      <DialogContent>
        <Stack
          spacing={1.35}
          sx={{ pt: 1 }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(
                '#38bdf8',
                0.2,
              )}`,
              bgcolor:
                'rgba(15,23,42,0.48)',
              px: 1.25,
              py: 1.05,
            }}
          >
            <Stack spacing={0.65}>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                {task?.title
                  ?? 'Tarea'}
              </Typography>

              <Stack
                direction="row"
                spacing={0.55}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  label={`Estándar · ${formatDateOnly(
                    previewQuery.data
                      ?.default_due_on,
                  )}`}
                />

                <Chip
                  size="small"
                  label={`Vigente · ${formatDateOnly(
                    previewQuery.data
                      ?.current_due_on,
                  )}`}
                  sx={{
                    color: '#bae6fd',
                    bgcolor:
                      alpha(
                        '#0284c7',
                        0.16,
                      ),
                  }}
                />

                <Chip
                  size="small"
                  label={
                    previewQuery.data
                      ?.is_blocking
                      ? 'Bloqueante'
                      : 'No bloqueante'
                  }
                  sx={{
                    color:
                      previewQuery.data
                        ?.is_blocking
                        ? '#fde68a'
                        : '#cbd5e1',
                    bgcolor:
                      previewQuery.data
                        ?.is_blocking
                        ? alpha(
                            '#f59e0b',
                            0.15,
                          )
                        : alpha(
                            '#64748b',
                            0.12,
                          ),
                  }}
                />
              </Stack>
            </Stack>
          </Paper>

          <RadioGroup
            value={adjustmentMode}
            onChange={(event) =>
              handleAdjustmentModeChange(
                event.target.value as
                  IndustrialWorkflowTaskDeadlineAdjustmentMode,
              )
            }
          >
            <Stack spacing={0.55}>
              <FormControlLabel
                value="manual"
                control={<Radio />}
                label="Definir una fecha específica"
              />

              <FormControlLabel
                value="restore_default"
                control={<Radio />}
                label="Restablecer el plazo estándar de esta tarea"
              />
            </Stack>
          </RadioGroup>

          <IndustrialWorkflowDatePicker
            label="Nuevo vencimiento de tarea"
            value={newDueOn}
            onChange={(value) => {
              setNewDueOn(value);
              setAcceptConflict(false);
            }}
            helperText="Fecha operativa vigente para esta tarea concreta."
          />

          {previewQuery.isFetching ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
            >
              <CircularProgress
                size={18}
              />

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                }}
              >
                Analizando impacto...
              </Typography>
            </Stack>
          ) : null}

          {previewQuery.isError ? (
            <Alert
              severity="error"
              variant="outlined"
            >
              {getErrorMessage(
                previewQuery.error,
              )}
            </Alert>
          ) : null}

          {previewQuery.data
            ?.conflicts
            .task_after_stage_due ? (
            <Alert
              severity="warning"
              variant="outlined"
              icon={
                <WarningAmberRounded />
              }
            >
              La tarea finalizará después del
              vencimiento vigente de la etapa.
              {previewQuery.data
                .conflicts
                .blocking_task_after_stage_due
                ? ' Además, se trata de una tarea bloqueante.'
                : ''}
            </Alert>
          ) : null}

          {previewQuery.data
            ?.conflicts
            .task_after_case_target ? (
            <Alert
              severity="warning"
              variant="outlined"
            >
              La tarea finalizará después del
              objetivo global del expediente.
            </Alert>
          ) : null}

          {requiresConflictAcceptance ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptConflict}
                  onChange={(event) =>
                    setAcceptConflict(
                      event.target.checked,
                    )
                  }
                />
              }
              label="Confirmo expresamente que deseo guardar esta excepción temporal."
            />
          ) : null}

          <TextField
            label="Motivo del ajuste"
            value={reason}
            onChange={(event) =>
              setReason(
                event.target.value,
              )
            }
            multiline
            minRows={3}
            error={shouldHighlightReason}
            helperText={
              shouldHighlightReason
                ? 'Indica el motivo del ajuste para poder guardar el nuevo plazo.'
                : 'Obligatorio. El motivo quedará registrado en la actividad del expediente.'
            }
            fullWidth
            required
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={
            adjustMutation.isPending
          }
          onClick={onClose}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          startIcon={
            adjustmentMode
            === 'restore_default'
              ? <RestoreRounded />
              : <CalendarMonthRounded />
          }
          disabled={
            adjustMutation.isPending
            || previewQuery.isFetching
            || previewQuery.isError
            || !workflowCase
              ?.current_stage_entry_token
            || !reason.trim()
            || (
              requiresConflictAcceptance
              && !acceptConflict
            )
          }
          onClick={() =>
            adjustMutation.mutate()
          }
        >
          Guardar ajuste
        </Button>
      </DialogActions>
    </Dialog>
  );
}